import React, { useRef, useState, useEffect } from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { ref, getDownloadURL, uploadBytes, deleteObject } from "firebase/storage";
import SortableBlock from "./SortableBlock";
import BlockWrapper from "./BlockWrapper";
import AutoTextarea from "../AutoTextarea";
import { storage } from "../../utils/firebase";
import { LOCALES, emptyLocale, getLocalizedText, setLocaleValue } from "../../utils/locale";

export interface FieldDef {
  key: string;
  label: string;
  input: string;
  localized?: boolean;
  editable?: boolean;
}

interface DynamicBlockProps {
  block: any;
  fields: FieldDef[];
  locale: string;
  project: string;
  contentType: string;
  onUpdate: (block: any) => void;
  parentPath: number[];
  hoverGroupPath?: string | null;
  blockConfig?: any[];
}

const ImageField: React.FC<{
  value: string;
  project: string;
  contentType: string;
  onChange: (url: string) => void;
}> = ({ value, project, contentType, onChange }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const hasImage = !!value && !value.startsWith("AUTO_") && value !== "AUTO_IMAGE_NAME" && value !== "AUTO_THUMB_NAME";

  useEffect(() => {
    if (!hasImage) { setPreviewUrl(null); return; }
    if (value.startsWith("http")) { setPreviewUrl(value); return; }
    let cancelled = false;
    getDownloadURL(ref(storage, value))
      .then((url) => { if (!cancelled) setPreviewUrl(url); })
      .catch(() => { if (!cancelled) setPreviewUrl(null); });
    return () => { cancelled = true; };
  }, [value, hasImage]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      const base = file.name.slice(0, file.name.lastIndexOf("."))
        .toLowerCase().replace(/[^a-z0-9_-]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
      const path = `content/${project}/${contentType}/images/${base}${ext}`;
      await uploadBytes(ref(storage, path), file);
      onChange(path);
    } catch (err) {
      console.error("[DynamicBlock] Upload error:", err);
    }
    setUploading(false);
  };

  return (
    <div className="block-image">
      <input ref={fileInput} type="file" accept="image/*" onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) handleUpload(f);
        e.target.value = "";
      }} style={{ display: "none" }} />
      {hasImage && previewUrl ? (
        <div className="block-image__preview-area">
          <img src={previewUrl} alt="" className="block-image__img" />
          <div className="block-image__overlay">
            <button className="block-image__overlay-btn" onClick={() => fileInput.current?.click()} title="Replace">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button className="block-image__overlay-btn block-image__overlay-btn--danger" onClick={async () => {
              if (!window.confirm("Delete this image?")) return;
              try { await deleteObject(ref(storage, value)); } catch {}
              onChange("");
            }} title="Remove">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <button className="block-image__upload" onClick={() => fileInput.current?.click()} disabled={uploading}>
          {uploading ? "Uploading..." : "Add image"}
        </button>
      )}
    </div>
  );
};

const GroupGap: React.FC<{ id: string }> = ({ id }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`editor__drop-gap${isOver ? " editor__drop-gap--active" : ""}`} />;
};

const EmptyGroupDrop: React.FC<{ id: string }> = ({ id }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`block-group__drop-hint${isOver ? " block-group__drop-hint--active" : ""}`}>
      Drop blocks here
    </div>
  );
};

const DynamicBlock: React.FC<DynamicBlockProps> = ({
  block, fields, locale, project, contentType, onUpdate, parentPath, hoverGroupPath, blockConfig
}) => {
  const content = block.content || [];
  const childIds = content.map((_: any, i: number) => `blk-${parentPath.join("-")}-${i}`);
  const pathKey = parentPath.join("-");

  const updateFieldValue = (key: string, value: any) => {
    onUpdate({ ...block, [key]: value });
  };

  const updateChild = (index: number, newChild: any) => {
    const next = [...content];
    next[index] = newChild;
    onUpdate({ ...block, content: next });
  };

  const deleteChild = (index: number) => {
    const next = [...content];
    next.splice(index, 1);
    onUpdate({ ...block, content: next });
  };

  const renderContentField = (field: FieldDef) => {
    return (
      <div key={field.key} className="dynamic-block__content-field">
        <span className="dynamic-block__content-label">{field.label}</span>
        <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
          <div className="block-group__children dynamic-block__content-zone">
            {content.length === 0 ? (
              <EmptyGroupDrop id={`group-${pathKey}`} />
            ) : (
              content.map((child: any, i: number) => (
                <React.Fragment key={childIds[i]}>
                  <GroupGap id={`ggap-${pathKey}-${i}`} />
                  <SortableBlock id={childIds[i]}>
                    <BlockWrapper
                      block={child}
                      index={i}
                      total={content.length}
                      blockPath={[...parentPath, i]}
                      project={project}
                      contentType={contentType}
                      locale={locale}
                      isDropTarget={Array.isArray(child.content) && hoverGroupPath === [...parentPath, i].join("-")}
                      hoverGroupPath={hoverGroupPath}
                      onUpdate={(b) => updateChild(i, b)}
                      onDelete={() => deleteChild(i)}
                      blockConfig={blockConfig}
                    />
                  </SortableBlock>
                </React.Fragment>
              ))
            )}
            {content.length > 0 && <GroupGap id={`ggap-${pathKey}-${content.length}`} />}
          </div>
        </SortableContext>
      </div>
    );
  };

  const renderField = (field: FieldDef) => {
    if (field.editable === false) return null;
    if (field.input === "content") return renderContentField(field);

    const val = block[field.key];

    if (field.localized) {
      const locVal = typeof val === "object" && val !== null && !Array.isArray(val) ? val : emptyLocale();
      const currentText = locVal[locale] || "";
      const fallbackText = locale !== "en" && !currentText ? locVal.en || "" : "";
      const isFallback = !currentText && !!fallbackText;

      const onChange = (newText: string) => {
        updateFieldValue(field.key, { ...locVal, [locale]: newText });
      };

      if (field.input === "textarea") {
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <div style={{ position: "relative" }}>
              <AutoTextarea
                className={`block-input block-input--text${isFallback ? " block-input--fallback" : ""}`}
                value={isFallback ? fallbackText : currentText}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => { if (isFallback) onChange(""); }}
                placeholder={field.label}
                rows={3}
              />
              {isFallback && <span className="block-fallback-badge">EN</span>}
            </div>
          </div>
        );
      }

      return (
        <div key={field.key} className="dynamic-block__field">
          <label className="dynamic-block__label">{field.label}</label>
          <div style={{ position: "relative" }}>
            <input
              className={`block-input block-input--small${isFallback ? " block-input--fallback" : ""}`}
              value={isFallback ? fallbackText : currentText}
              onChange={(e) => onChange(e.target.value)}
              onFocus={() => { if (isFallback) onChange(""); }}
              placeholder={field.label}
            />
            {isFallback && <span className="block-fallback-badge">EN</span>}
          </div>
        </div>
      );
    }

    switch (field.input) {
      case "number":
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <input
              type="number"
              className="block-input block-input--small"
              value={val ?? ""}
              onChange={(e) => updateFieldValue(field.key, e.target.value === "" ? null : Number(e.target.value))}
              placeholder={field.label}
            />
          </div>
        );

      case "color":
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <div className="dynamic-block__color-row">
              <input
                type="color"
                className="block-group__color-picker"
                value={val || "#ffffff"}
                onChange={(e) => updateFieldValue(field.key, e.target.value)}
              />
              <input
                className="block-input block-input--tiny"
                value={val || ""}
                onChange={(e) => updateFieldValue(field.key, e.target.value)}
                placeholder="#000000"
              />
              {val && (
                <button className="block-group__color-clear" onClick={() => updateFieldValue(field.key, null)} title="Clear">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        );

      case "json":
        // Shopping list items — render as editable rows
        if (block.type === "shopping_list" && field.key === "action") {
          const sl = val?.shopping_list || {};
          const items: any[] = sl.items || [];
          const updateItems = (newItems: any[]) => {
            updateFieldValue("action", { ...val, shopping_list: { ...sl, items: newItems } });
          };
          const updateItemLoc = (i: number, key: string, text: string) => {
            const next = [...items];
            next[i] = { ...next[i], [key]: setLocaleValue(next[i][key], locale, text) };
            updateItems(next);
          };
          const updateItemRaw = (i: number, key: string, v: string) => {
            const next = [...items];
            next[i] = { ...next[i], [key]: v };
            updateItems(next);
          };
          return (
            <div key={field.key} className="dynamic-block__field">
              <label className="dynamic-block__label">Items ({items.length})</label>
              <div className="dynamic-block__items-list">
                {items.map((item: any, i: number) => (
                  <div key={i} className="dynamic-block__item-row">
                    <input className="block-input block-input--tiny" value={item.emoji || ""} onChange={(e) => updateItemRaw(i, "emoji", e.target.value)} placeholder="🥚" style={{ width: 32, textAlign: "center", padding: "3px 2px" }} />
                    <input className="block-input block-input--small" value={getLocalizedText(item.name, locale)} onChange={(e) => updateItemLoc(i, "name", e.target.value)} placeholder="Name" style={{ flex: 1, minWidth: 0 }} />
                    <input className="block-input block-input--small" value={getLocalizedText(item.quantity, locale)} onChange={(e) => updateItemLoc(i, "quantity", e.target.value)} placeholder="Qty" style={{ flex: "0 0 30%" }} />
                    <button className="dynamic-block__item-remove" onClick={() => updateItems(items.filter((_, j) => j !== i))}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </div>
                ))}
                <button className="dynamic-block__item-add" onClick={() => updateItems([...items, { id: `item_${Date.now()}`, name: emptyLocale(), emoji: "", quantity: "" }])}>+ Add item</button>
              </div>
            </div>
          );
        }
        // Day meals — render as editable cards
        if (block.type === "day" && field.key === "meals") {
          const meals: any[] = val || [];
          const updateMeals = (newMeals: any[]) => updateFieldValue("meals", newMeals);
          const updateMealLoc = (i: number, key: string, text: string) => {
            const next = [...meals];
            next[i] = { ...next[i], [key]: setLocaleValue(next[i][key], locale, text) };
            updateMeals(next);
          };
          return (
            <div key={field.key} className="dynamic-block__field">
              <label className="dynamic-block__label">Meals ({meals.length})</label>
              <div className="dynamic-block__meals-list">
                {meals.map((meal: any, i: number) => (
                  <div key={i} className="dynamic-block__meal-card">
                    <div className="dynamic-block__meal-row">
                      <input className="block-input block-input--small" value={getLocalizedText(meal.title, locale)} onChange={(e) => updateMealLoc(i, "title", e.target.value)} placeholder="Title" style={{ width: 120 }} />
                      <input className="block-input block-input--small" value={getLocalizedText(meal.description, locale)} onChange={(e) => updateMealLoc(i, "description", e.target.value)} placeholder="Description" style={{ flex: 1 }} />
                      <button className="dynamic-block__item-remove" onClick={() => updateMeals(meals.filter((_, j) => j !== i))}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
                <button className="dynamic-block__item-add" onClick={() => updateMeals([...meals, { title: emptyLocale(), description: emptyLocale(), details: [], nutrition: {} }])}>+ Add meal</button>
              </div>
            </div>
          );
        }
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <textarea
              className="block-input block-input--text"
              value={val !== null && val !== undefined ? (typeof val === "string" ? val : JSON.stringify(val, null, 2)) : ""}
              onChange={(e) => {
                try { updateFieldValue(field.key, JSON.parse(e.target.value)); }
                catch { updateFieldValue(field.key, e.target.value); }
              }}
              rows={3}
              placeholder="JSON value"
            />
          </div>
        );

      case "textarea":
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <AutoTextarea
              className="block-input block-input--text"
              value={val || ""}
              onChange={(e) => updateFieldValue(field.key, e.target.value)}
              placeholder={field.label}
              rows={3}
            />
          </div>
        );

      case "image":
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <ImageField
              value={val || ""}
              project={project}
              contentType={contentType}
              onChange={(url) => updateFieldValue(field.key, url)}
            />
          </div>
        );

      default:
        return (
          <div key={field.key} className="dynamic-block__field">
            <label className="dynamic-block__label">{field.label}</label>
            <input
              className="block-input block-input--small"
              value={val || ""}
              onChange={(e) => updateFieldValue(field.key, e.target.value)}
              placeholder={field.label}
            />
          </div>
        );
    }
  };

  return (
    <div className="dynamic-block">
      <div className="dynamic-block__fields">
        {fields.map(renderField)}
      </div>
    </div>
  );
};

export default DynamicBlock;
